"use client";

import {
  Modal,
  Box as MantineBox,
  Image as MantineImage,
  List as MantineList,
  Text as MantineText,
  Title as MantineTitle,
  UnstyledButton as MantineUnstyledButton,
} from "@mantine/core";
import {
  IconBolt,
  IconPhotoSearch,
  IconEye,
  IconEyeOff,
  IconHomePlus,
  IconBell,
} from "@tabler/icons-react";
import { useMediaQuery } from "react-responsive";
import PlusIcon from "@/icons/Plus";
import styles from "./styles.module.scss";

interface Props {
  opened: boolean;
  onClose: () => void;
  isProUser?: boolean;
}

const FEATURES: {
  name: string;
  proOnly?: boolean;
  icon: React.ReactNode;
  image?: string;
  description: React.ReactNode;
}[] = [
  {
    name: "SAVED FILTERS",
    icon: <PlusIcon color="black" width={18} height={18} />,
    image: "/search-tips/saved-filters.png",
    description:
      "Sign in to store up to 20 carefully crafted filter combinations and return to them instantly. Use the dropdown to seamlessly switch between them. Pro users unlock faster push notification intervals on saved filters.",
  },
  {
    name: "KURBO MODE",
    proOnly: true,
    icon: <IconBolt size={18} />,
    description: (
      <>
        Reveal listings added in the last hour that match your active filters. Each page costs 1
        token from your daily budget, shown via the bolt{" "}
        <MantineText span className={styles.inline}>
          <IconBolt size={14} />
        </MantineText>{" "}
        in the nav. Tokens reset at midnight. Try Pro free for 14 days for a larger daily budget.
      </>
    ),
  },
  {
    name: "EXCLUSION FILTERS",
    icon: <IconEye size={18} />,
    image: "/search-tips/exclusion-filters.png",
    description: (
      <>
        For brand, vendor, size and country filters, click a selected option again to flip it from{" "}
        <MantineText span className={styles.inline}>
          <IconEye size={14} />
        </MantineText>{" "}
        included to{" "}
        <MantineText span className={styles.inline}>
          <IconEyeOff size={14} />
        </MantineText>{" "}
        excluded. Everything you don&apos;t want, gone.
      </>
    ),
  },
  {
    name: "IMAGE SEARCH",
    icon: <IconPhotoSearch size={18} />,
    image: "/search-tips/image-search.png",
    description: (
      <>
        Upload, paste or link any image using{" "}
        <MantineText span className={styles.inline}>
          <IconPhotoSearch size={14} />
        </MantineText>{" "}
        to find visually similar pieces across our entire database. The same icon on any search card
        runs an image search on that item.
      </>
    ),
  },
  {
    name: "APP INSTALL",
    icon: <IconHomePlus size={18} />,
    image: "/search-tips/app-install.png",
    description:
      "Add KURB to your home screen for one-tap access and push notifications. No app store needed, works on iPhone and Android.",
  },
  {
    name: "SEARCH NOTIFICATIONS",
    icon: <IconBell size={18} />,
    image: "/search-tips/search-notifications.png",
    description:
      "Get notified when items matching your saved filters enter the market or drop in price. Hourly and 2-hour intervals are free for all signed-in users. Instant, 15-minute, and 30-minute intervals are available on Pro.",
  },
];

export default function SearchTipsModal({ opened, onClose, isProUser = false }: Props) {
  const isSmall = useMediaQuery({ query: "(min-width: 600px)" });
  const isMedium = useMediaQuery({ query: "(min-width: 850px)" });
  const modalSize = isMedium ? 720 : isSmall ? "calc(100vw - 64px)" : "calc(100vw - 2rem)";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      centered
      padding={0}
      zIndex={9000}
      size={modalSize}
      className={styles.modal}
    >
      <MantineUnstyledButton className={styles.closeButton} onClick={onClose} aria-label="Close">
        <MantineImage src="/cross.svg" alt="Close" w={20} h={20} />
      </MantineUnstyledButton>
      <MantineBox className={styles.header}>
        <MantineTitle order={2} className={styles.title}>
          Tips &amp; Tricks
        </MantineTitle>
        <MantineText className={styles.subtitle}>Tips for speeding up your search.</MantineText>
      </MantineBox>

      <MantineList className={styles.featureList}>
        {FEATURES.filter((f) => !f.proOnly || isProUser).map((f) => (
          <MantineList.Item key={f.name} className={styles.feature}>
            <MantineBox className={styles.featureBody}>
              <MantineText span className={styles.featureName}>
                {f.name}
                <MantineText span className={styles.featureIcon} aria-hidden="true">
                  {f.icon}
                </MantineText>
              </MantineText>
              <MantineText className={styles.featureDesc}>{f.description}</MantineText>
            </MantineBox>
            {f.image && <MantineImage src={f.image} alt={f.name} className={styles.featureImage} />}
          </MantineList.Item>
        ))}
      </MantineList>
    </Modal>
  );
}
